/**
 * Small observable stores for UI selections that must survive a reload: the UI locale
 * (Req 22.2) and the tenant switcher. Values are non-secret and kept in localStorage.
 * They follow the `subscribe/getSnapshot` contract of React's `useSyncExternalStore`.
 */
import { DEFAULT_LOCALE, resolveLocale } from '@macksons/i18n';
/** `localStorage` when available (browser), else a throwaway in-memory map (SSR/tests). */
export function defaultStorage() {
    try {
        if (typeof localStorage !== 'undefined')
            return localStorage;
    }
    catch {
        // Access can throw in sandboxed iframes / privacy modes.
    }
    const map = new Map();
    return {
        getItem: (k) => map.get(k) ?? null,
        setItem: (k, v) => void map.set(k, v),
        removeItem: (k) => void map.delete(k),
    };
}
function createStore(storage, key, decode, encode) {
    const listeners = new Set();
    let stored = null;
    try {
        stored = storage.getItem(key);
    }
    catch {
        // Blocked storage: start from the default.
    }
    let value = decode(stored);
    return {
        get: () => value,
        set(next) {
            if (Object.is(next, value))
                return;
            value = next;
            try {
                const raw = encode(next);
                if (raw === null)
                    storage.removeItem(key);
                else
                    storage.setItem(key, raw);
            }
            catch {
                // Storage full or blocked: keep the in-memory value.
            }
            for (const l of listeners)
                l();
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
/**
 * Persisted UI locale. The initial value is the stored locale, else the browser language
 * (`navigatorLanguage`), else `en`.
 */
export function createLocaleStore(storage = defaultStorage(), navigatorLanguage = typeof navigator === 'undefined' ? undefined : navigator.language, key = 'macksons.locale') {
    return createStore(storage, key, (raw) => resolveLocale(raw ?? navigatorLanguage, DEFAULT_LOCALE), (v) => v);
}
/**
 * Persisted tenant selection; `undefined` means none selected (or "all tenants" for
 * group_admin). Callers validate the stored id against the tenants the user can see.
 */
export function createTenantStore(storage = defaultStorage(), key = 'macksons.tenant') {
    return createIdStore(storage, key);
}
/**
 * Persisted outlet selection of the Operator_Console (outlet_staff / dealer_staff act for
 * one of their `outlet_ids` at a time). Callers validate it against the token's outlets.
 */
export function createOutletStore(storage = defaultStorage(), key = 'macksons.outlet') {
    return createIdStore(storage, key);
}
function createIdStore(storage, key) {
    return createStore(storage, key, (raw) => (raw && /^[0-9a-f-]{36}$/i.test(raw) ? raw.toLowerCase() : undefined), (v) => v ?? null);
}
//# sourceMappingURL=selection-store.js.map